import { Chip, Divider, LinearProgress, Skeleton } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { MouseEvent } from 'react';

import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';

import { formatDealAmount } from 'helper/short-currency';

import zeplinColor from 'theme/Color';

import DeleteIcon from 'assets/delete-red.png';
import { ReactComponent as EditIcon } from 'assets/edit.svg';
import { ReactComponent as VerticalDots } from 'assets/vertical-dots-white.svg';

import { Utilities } from 'utilities/utilities';
import { getHealthBarColor, getStatusChipColor } from '../clientHealth';
import { usePermissions } from 'customHook/usePermissions';

interface ClientSelectedHeaderProps {
  details: any;
  captive8loading: boolean;
  menuAnchor: null | HTMLElement;
  menuOpen: boolean;
  setMenuAnchor: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  onEdit: () => void;
  handleDeleteClick: (event: MouseEvent<HTMLDivElement>) => void;
  accordionExpanded: boolean;
  setAccordionExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

const ClientSelectedHeader = ({
  details,
  captive8loading,
  menuAnchor,
  menuOpen,
  setMenuAnchor,
  onEdit,
  handleDeleteClick,
  accordionExpanded,
  setAccordionExpanded,
}: ClientSelectedHeaderProps) => {
  const { hasAction, hasFullAccess } = usePermissions();
  const canEditClient = hasAction('clients', 'clientlist', 'edit') || hasFullAccess('clients');
  const canDeleteClient = hasAction('clients', 'clientlist', 'delete') || hasFullAccess('clients');
  const profileDetails = (
    <Box sx={{ display: 'flex', gap: '16px' }}>
      <Box
        sx={{
          width: { xs: '40px', xl: '60px' },
          height: { xs: '40px', xl: '60px' },
          borderRadius: '50%',
          backgroundColor: zeplinColor.Neutral10,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {captive8loading ? (
          <Skeleton variant='rounded' width={100} height={100} />
        ) : details?.profileImageUrl ? (
          <img
            src={details?.profileImageUrl}
            alt='logo'
            loading='lazy'
            style={{ objectFit: 'fill', width: '100%', height: '100%', borderRadius: '50%' }}
          />
        ) : (
          <Typography variant='display4' sx={{ color: zeplinColor.Neutral1 }}>
            {details?.name
              ?.split(' ')
              .map((val: any, index: number) =>
                index === 0 || index === details?.name?.split(' ').length - 1 ? val[0] : '',
              )
              .join('')}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Typography variant='responsiveHeading1' color={zeplinColor.Neutral1}>
          {details?.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Chip
            label={
              <Typography variant='responsiveLabel1' color={zeplinColor.Neutral1}>
                {details?.clientType
                  ? `${details?.clientType?.slice(0, 1)?.toUpperCase()}${details?.clientType?.slice(1)}`
                  : 'NA'}
              </Typography>
            }
            sx={{
              backgroundColor: zeplinColor.Neutral10,
              padding: '0px',
              borderRadius: '50px',
              width: 'fit-content',
            }}
          />
          <Chip
            label={
              <Typography variant='responsiveLabel1' color={zeplinColor.BasicBlack}>
                {details?.agency
                  ? `${details?.agency?.slice(0, 1)?.toUpperCase()}${details?.agency?.slice(1)}`
                  : 'NA'}
              </Typography>
            }
            sx={{
              backgroundColor: zeplinColor.Neutral1,
              padding: '0px',
              borderRadius: '50px',
              width: 'fit-content',
            }}
          />
        </Box>
      </Box>
    </Box>
  );

  const enterprise = details?.enterprise;
  const accordionSummaryItems = [
    `Enterprise`,
    `Combined Rev: ${formatDealAmount(enterprise?.combinedRevenue)}`,
    enterprise?.ownedRevenuePercent != null
      ? `Owned: ${enterprise.ownedRevenuePercent}%`
      : 'Owned: N/A',
    `Entities`,
    `Active Deals`,
  ];

  return (
    <Box
      sx={{
        top: '16px',
        backgroundColor: zeplinColor.BasicBlack,
        color: zeplinColor.Neutral1,
        padding: '20px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      {/* Row 1: profile + stat cards */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        {profileDetails}

        <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignSelf: 'stretch' }}>
          {/* Health */}
          <Box
            sx={{
              borderRadius: '10px',
              backgroundColor: zeplinColor.Neutral11,
              padding: '8px 12px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flex: 1,
              boxSizing: 'border-box',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <LinearProgress
                variant='determinate'
                value={details?.healthScore || 0}
                sx={{
                  flex: 1,
                  borderRadius: '100px',
                  backgroundColor: zeplinColor.Neutral8,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getHealthBarColor(details?.healthScore ?? 'Need Attention'),
                    borderRadius: '100px',
                  },
                }}
              />
              <Typography
                variant='responsiveHeading4'
                color={getHealthBarColor(details?.healthScore ?? 'Need Attention')}
              >
                {details?.healthScore ?? 0}%
              </Typography>
            </Box>
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
              Health
            </Typography>
          </Box>

          {/* Status */}
          <Box
            sx={{
              borderRadius: '10px',
              backgroundColor: zeplinColor.Neutral11,
              padding: '8px 12px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flex: 1,
              boxSizing: 'border-box',
            }}
          >
            <Chip
              label={
                <Typography
                  variant='responsiveLabel1'
                  color={zeplinColor.Neutral1}
                  sx={{
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {details?.healthStatus ?? 'NA'}
                </Typography>
              }
              sx={{
                backgroundColor: getStatusChipColor(details?.healthStatus ?? 'Need Attention'),
                padding: '5px 12px',
                borderRadius: '50px',
                height: '24px',
                width: 'fit-content',
              }}
            />
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
              Status
            </Typography>
          </Box>

          {/* TTM Revenue */}
          <Box
            sx={{
              borderRadius: '10px',
              backgroundColor: zeplinColor.Neutral11,
              padding: '8px 12px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flex: 1,
              boxSizing: 'border-box',
            }}
          >
            <Typography variant='responsiveHeading4' color={zeplinColor.Neutral1}>
              {formatDealAmount(details?.ttmRevenue)}
            </Typography>
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
              TTM Revenue
            </Typography>
          </Box>

          {/* Active Deals */}
          <Box
            sx={{
              borderRadius: '10px',
              backgroundColor: zeplinColor.Neutral11,
              padding: '8px 12px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flex: 1,
              boxSizing: 'border-box',
            }}
          >
            <Typography variant='responsiveHeading4' color={zeplinColor.Neutral1}>
              {details?.activeDealCount ?? 0}
            </Typography>
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
              Active Deals
            </Typography>
          </Box>

          <Box
            sx={{
              borderRadius: '10px',
              backgroundColor: zeplinColor.Neutral11,
              padding: '8px 12px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flex: 1,
              boxSizing: 'border-box',
            }}
          >
            <Typography variant='responsiveHeading4' color={zeplinColor.Neutral1}>
              {details?.lastTouch ? Utilities.formatDate(details.lastTouch) : 'NA'}
            </Typography>
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
              Last Touch
            </Typography>
          </Box>

          {/* Options menu */}
          {canEditClient || canDeleteClient ? (
            <LightTooltip
              title={
                <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                  Options
                </Typography>
              }
              placement='bottom-end'
              style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
            >
              <VerticalDots
                style={{
                  cursor: 'pointer',
                  alignSelf: 'center',
                  flexShrink: 0,
                  fill: zeplinColor.Neutral1,
                }}
                onClick={(e: any) => {
                  e.stopPropagation();
                  setMenuAnchor(e.currentTarget);
                }}
              />
            </LightTooltip>
          ) : null}

          <CustomPopOver
            onClick={(e: any) => e.stopPropagation()}
            setAnchorEl={setMenuAnchor}
            anchorEl={menuAnchor}
            id={menuOpen ? 'simple-popover' : undefined}
            sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px' }}>
              {canEditClient && (
                <Box
                  sx={{ display: 'flex', gap: '9px', cursor: 'pointer', alignItems: 'center' }}
                  onClick={() => {
                    setMenuAnchor(null);
                    onEdit();
                  }}
                >
                  <EditIcon width={18} height={18} />
                  <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                    Edit Client
                  </Typography>
                </Box>
              )}
              {/* <Box
                sx={{ display: 'flex', gap: '9px', cursor: 'pointer', alignItems: 'center' }}
                onClick={() => {
                  setMenuAnchor(null);
                  navigator.clipboard.writeText(window.location.href);
                }}
              >
                <img src='/assets/copy-link.svg' width={18} height={18} alt='copy' />
                <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                  Copy Link
                </Typography>
              </Box> */}
              {canDeleteClient && (
                <Box
                  sx={{ display: 'flex', gap: '9px', cursor: 'pointer', alignItems: 'center' }}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    setMenuAnchor(null);
                    handleDeleteClick(e);
                  }}
                >
                  <img src={DeleteIcon} width={18} height={18} alt='archive' />
                  <Typography variant='responsiveBody3' color={zeplinColor.Error}>
                    Archive Client
                  </Typography>
                </Box>
              )}
            </Box>
          </CustomPopOver>
        </Box>
      </Box>

      {/* Divider */}
      <Divider component='div' sx={{ borderColor: zeplinColor.Neutral10, position: 'relative' }} />

      {/* Enterprise accordion */}
      <Accordion
        expanded={accordionExpanded}
        sx={{ backgroundColor: zeplinColor.BasicBlack, color: zeplinColor.Neutral1 }}
      >
        <AccordionSummary
          aria-controls='panel1-content'
          id='panel1-header'
          sx={{
            '& .MuiAccordionSummary-content': { margin: 0 },
            cursor: 'default',
            '&:hover': { cursor: 'default' },
            pointerEvents: 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {accordionSummaryItems.map((item, index, arr) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography sx={{ fontWeight: 500, fontSize: '14px', color: zeplinColor.Neutral1 }}>
                  {item}
                </Typography>
                {index < arr.length - 1 && (
                  <Box
                    sx={{
                      width: '5px',
                      height: '5px',
                      borderRadius: '50%',
                      backgroundColor: zeplinColor.Neutral8,
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            ))}
            <Box
              sx={{
                width: '24px',
                height: '24px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '38px',
                backgroundColor: zeplinColor.Neutral9,
                border: `1px solid ${zeplinColor.Neutral7}`,
                flexShrink: 0,
                cursor: 'pointer',
                pointerEvents: 'auto',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setAccordionExpanded((prev) => !prev);
              }}
            >
              <img
                src='/assets/down-arrow-white.svg'
                alt='expand'
                width='16px'
                height='16px'
                style={{
                  transform: accordionExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s',
                }}
              />
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ padding: '8px 0 0 0' }}>
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {(details?.enterprise?.entities ?? []).map((entity: any, index: number) => {
              const initials = entity.name
                ?.split(' ')
                .map((w: string, i: number, arr: string[]) =>
                  i === 0 || i === arr.length - 1 ? w[0] : '',
                )
                .join('');
              return (
                <Box
                  key={entity.clientId}
                  sx={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    padding: '8px 12px',
                    backgroundColor: zeplinColor.Neutral11,
                    borderRadius: '10px',
                    width: '177px',
                  }}
                >
                  <Box
                    sx={{
                      width: '43px',
                      height: '43px',
                      borderRadius: '29px',
                      backgroundColor: entity.profileImageUrl
                        ? 'transparent'
                        : (zeplinColor as any)[`USER_PROFILE_COLOR${(index % 13) + 1}`],
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexShrink: 0,
                      overflow: 'hidden',
                    }}
                  >
                    {entity.profileImageUrl ? (
                      <img
                        src={entity.profileImageUrl}
                        alt={entity.name}
                        style={{
                          width: '43px',
                          height: '43px',
                          objectFit: 'cover',
                          borderRadius: '29px',
                        }}
                      />
                    ) : (
                      <Typography variant='responsiveBody3' sx={{ color: zeplinColor.Neutral1 }}>
                        {initials}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Typography
                      variant='responsiveHeading4'
                      color={zeplinColor.Neutral1}
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '120px',
                      }}
                    >
                      {entity.name}
                    </Typography>
                    <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                      {formatDealAmount(entity.ttmRevenue)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ClientSelectedHeader;
